import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const SCOPE_TYPES = ["VISUALIZATION", "MARKETING", "VEXOS", "ANIMATION"];
const DELIVERABLE_TYPES = {
  VISUALIZATION: ["FLOORPLAN", "RENDERING", "360_TOUR", "3D_MODEL"],
  MARKETING: ["SOCIAL_MEDIA", "BROCHURE", "WEBSITE", "GALLERY"],
  VEXOS: ["INTERACTIVE_TOUR", "SALES_GALLERY", "VR_EXPERIENCE"],
  ANIMATION: ["FLYTHROUGH", "WALKTHROUGH", "PRODUCT_ANIMATION"]
};

export function CreateMilestoneDialog({ open, onOpenChange, selectedDate, onMilestoneCreated }) {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    program_id: "",
    scope_type: "VISUALIZATION",
    deliverable_type: "",
    title: "",
    residence_number: "",
    due_date: selectedDate || "",
    notes: ""
  });

  const fetchPrograms = useCallback(async () => {
    try {
      // Fetch ALL programs for milestone creation (no stage filtering)
      const res = await apiClient.get("/programs");
      // Sort by most recent first
      const sortedPrograms = res.data.sort((a, b) => {
        // Active programs first, then by name
        const stageOrder = { "Production": 0, "Kickoff": 1, "Queued": 2, "Delivered": 3, "Closed": 4 };
        const aOrder = stageOrder[a.stage] ?? 5;
        const bOrder = stageOrder[b.stage] ?? 5;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (a.program_name || "").localeCompare(b.program_name || "");
      });
      setPrograms(sortedPrograms);
    } catch (err) {
      toast.error("Failed to load programs");
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchPrograms();
      setFormData(prev => ({ ...prev, due_date: selectedDate || "" }));
    }
  }, [open, selectedDate, fetchPrograms]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.program_id || !formData.title || !formData.due_date) {
      toast.error("Please fill in required fields");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        program_id: formData.program_id,
        scope_type: formData.scope_type,
        deliverable_type: formData.deliverable_type || "CUSTOM",
        item_index: 0,
        title: formData.title,
        residence_number: formData.residence_number || null,
        due_date: formData.due_date,
        notes: formData.notes || null
      };

      await apiClient.post(`/programs/${formData.program_id}/milestones`, payload);
      toast.success("Milestone created successfully");
      onOpenChange(false);
      onMilestoneCreated?.();
      
      // Reset form
      setFormData({
        program_id: "",
        scope_type: "VISUALIZATION",
        deliverable_type: "",
        title: "",
        residence_number: "",
        due_date: "",
        notes: ""
      });
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to create milestone";
      toast.error(msg);
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Deliverable Milestone</DialogTitle>
          <DialogDescription>Add a new milestone to the calendar</DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="program">Program *</Label>
            <Select
              value={formData.program_id}
              onValueChange={(val) => setFormData(prev => ({ ...prev, program_id: val }))}
            >
              <SelectTrigger data-testid="milestone-program-select">
                <SelectValue placeholder="Select program" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {programs.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="flex items-center gap-2">
                      {p.program_name}
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        p.stage === "Production" ? "bg-green-100 text-green-700" :
                        p.stage === "Kickoff" ? "bg-blue-100 text-blue-700" :
                        p.stage === "Queued" ? "bg-yellow-100 text-yellow-700" :
                        "bg-gray-100 text-gray-600"
                      }`}>
                        {p.stage || "Unknown"}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="scope">Scope Type</Label>
              <Select
                value={formData.scope_type}
                onValueChange={(val) => setFormData(prev => ({ ...prev, scope_type: val, deliverable_type: "" }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SCOPE_TYPES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="deliv_type">Deliverable Type</Label>
              <Select
                value={formData.deliverable_type}
                onValueChange={(val) => setFormData(prev => ({ ...prev, deliverable_type: val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {(DELIVERABLE_TYPES[formData.scope_type] || []).map(t => (
                    <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              data-testid="milestone-title-input"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g., Interior Render - Unit A"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="due_date">Due Date *</Label>
              <Input
                id="due_date"
                type="date"
                data-testid="milestone-due-date-input"
                value={formData.due_date}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="residence">Unit/Residence #</Label>
              <Input
                id="residence"
                value={formData.residence_number}
                onChange={(e) => setFormData(prev => ({ ...prev, residence_number: e.target.value }))}
                placeholder="e.g., Unit 101"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="create-milestone-btn">
              {loading ? "Creating..." : "Create Milestone"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
